/**
 * Dark Pool Screen - Crypto auction betting with real coins
 * Player bets real coins, winnings = bet * multiplier.
 * Ad reveals insider info. Coin icons = initial circles (no emoji);
 * exchange-linked coins show their localized exchange name.
 */
import { DARKPOOL_CONFIG, dpExchangeCoinId, getMaxBetRatio, getRewardMultiplier } from '../game/config/darkpool.js';
import { getCoinConfig } from '../game/config/coins.js';
import type { DarkPoolCoin } from '../game/state.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { onRender, getState, getViewState, dispatch, setInnerHTML, requestRender, markScreenDirty, showToast, showModal } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';

let container: HTMLDivElement | null = null;
let selectedCoinId: string | null = null;
let betPercent = 10; // default bet % of coins

/** Display name: localized exchange name for linked coins, else pool name */
function dpCoinName(coin: DarkPoolCoin): string {
  const exId = dpExchangeCoinId(coin.id);
  const cfg = exId ? getCoinConfig(exId) : undefined;
  return cfg ? t(cfg.nameKey) : coin.name;
}

/** Initial circle icon (colored background + 2 letters), exchange-coin style */
function dpCoinIconHtml(coin: DarkPoolCoin, extraClass = ''): string {
  const initials = coin.name.slice(0, 2).toUpperCase();
  return `<span class="dp-coin-initial${extraClass}" style="background:${coin.color}">${initials}</span>`;
}

export function initDarkPoolScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'darkpool-screen';
  container.className = 'screen-panel';
  content.appendChild(container);

  onRender(renderDarkPool, { alwaysTabs: ['darkpool'] });
}

let tutorialShown = false;

function showTutorial() {
  showModal(
    t('dp_tutorial_title'),
    `<div class="modal-detail" style="text-align:left;line-height:1.6">
      <p>${t('dp_tutorial_1')}</p>
      <p>${t('dp_tutorial_2')}</p>
      <p>${t('dp_tutorial_3')}</p>
      <p>${t('dp_tutorial_4')}</p>
    </div>`,
    [{ label: t('confirm'), color: 'var(--cyan)', onClick: () => {
      dispatch({ type: 'DARKPOOL_TUTORIAL_SEEN' });
    }}]
  );
}

function renderDarkPool() {
  if (!container) return;
  const st = getState();
  const dp = st.darkPool;
  if (!dp) return;
  const now = Date.now();

  // Reset guard when data is cleared (tutorialSeen goes back to false)
  if (dp.tutorialSeen) tutorialShown = false;

  // Show tutorial only when player enters darkpool tab (not on background render)
  if (!dp.tutorialSeen && !tutorialShown && getViewState().currentTab === 'darkpool') {
    tutorialShown = true;
    showTutorial();
  }

  // Reset selection when not in betting phase
  if (dp.phase !== 'betting') {
    selectedCoinId = null;
  }

  let html = '';

  // Header with stats
  html += `<div class="dp-header">
    <div class="dp-title">${t('dp_title')} <button class="dp-help-btn" data-action="help">?</button></div>
    <div class="dp-stats">
      <span>${t('dp_total_bets')}: ${dp.totalBets}</span>
      <span>${t('dp_wins')}: ${dp.totalWins}</span>
      <span class="${dp.totalProfit >= 0 ? 'text-green' : 'text-red'}">${t('dp_profit')}: ${dp.totalProfit >= 0 ? '+' : ''}${formatNumber(dp.totalProfit)}</span>
    </div>
  </div>`;

  // === IDLE PHASE ===
  if (dp.phase === 'idle') {
    const timeLeft = Math.max(0, Math.ceil((dp.nextRoundTime - now) / 1000));
    html += `<div class="dp-idle">
      <div class="dp-idle-text">${t('dp_next_round')}</div>
      <div class="dp-idle-timer">${timeLeft}s</div>
      ${timeLeft > 5 ? `<button class="ad-btn dp-btn-skip" data-action="skip">${t('dp_skip_cooldown')}</button>` : ''}
      <div class="dp-idle-hint">${t('dp_bet_hint')}</div>
    </div>`;
  }

  // === BETTING PHASE ===
  if (dp.phase === 'betting') {
    const timeLeft = Math.max(0, Math.ceil((dp.phaseEndTime - now) / 1000));
    const hasBet = !!dp.playerBet;

    html += `<div class="dp-phase-label dp-phase-betting">${t('dp_betting')} <span class="dp-timer">${timeLeft}s</span></div>`;

    // Coins to bet on
    html += `<div class="dp-coins">`;
    for (const coin of dp.coins) {
      const isSelected = selectedCoinId === coin.id;
      const isBet = dp.playerBet?.coinId === coin.id;
      const cls = isBet ? 'dp-coin dp-coin-bet' : isSelected ? 'dp-coin dp-coin-selected' : 'dp-coin';
      html += `<div class="${cls}" data-coin="${coin.id}">
        <div class="dp-coin-icon">${dpCoinIconHtml(coin)}</div>
        <div class="dp-coin-name">${dpCoinName(coin)}</div>
        ${isBet ? `<div class="dp-coin-badge">${t('dp_your_bet')}</div>` : ''}
      </div>`;
    }
    html += `</div>`;

    // Insider info
    if (dp.insiderInfo) {
      const infoCoin = dp.coins.find(c => c.id === dp.insiderInfo!.coinId);
      const infoIcon = infoCoin ? dpCoinIconHtml(infoCoin, ' dp-coin-initial-sm') : '?';
      const infoName = infoCoin ? dpCoinName(infoCoin) : '?';
      html += `<div class="dp-insider">
        <div class="dp-insider-label">${t('dp_insider_title')}</div>
        <div class="dp-insider-text">${infoIcon} ${infoName} ${t('dp_insider_hint')} x${dp.insiderInfo.hintMultiplier}</div>
        <div class="dp-insider-warn">${t('dp_insider_warn')}</div>
      </div>`;
    }

    // Bet controls (only if no bet placed)
    if (!hasBet) {
      const maxBetRatio = getMaxBetRatio(st.purchasedUpgrades);
      const maxBet = Math.floor(st.coins * maxBetRatio);
      const minBet = Math.floor(st.coins * DARKPOOL_CONFIG.minBetRatio);
      const betAmount = Math.floor(st.coins * betPercent / 100);
      const clampedBet = Math.max(minBet, Math.min(betAmount, maxBet));
      const canBet = selectedCoinId && clampedBet > 0 && st.coins > 0;
      const rewardMult = getRewardMultiplier(st.purchasedUpgrades);
      const previewReward = clampedBet * 3 * rewardMult; // rough preview at 3x

      html += `<div class="dp-bet-controls">
        <div class="dp-bet-label">${t('dp_bet_coins')} (${formatNumber(st.coins)} ${t('dp_available')})</div>
        <div class="dp-bet-slider-row">
          <input type="range" class="dp-slider" id="dp-bet-slider" min="1" max="${Math.floor(maxBetRatio * 100)}" value="${betPercent}" step="1" />
          <span class="dp-bet-value">${betPercent}% = ${formatNumber(clampedBet)}</span>
        </div>
        <div class="dp-bet-preview">${t('dp_win_preview')}: ~${formatNumber(previewReward)} (x3)</div>
        <div class="dp-bet-btns">
          ${!dp.insiderInfo ? `<button class="ad-btn" data-action="insider">${t('dp_get_insider')}</button>` : ''}
          <button class="dp-btn dp-btn-bet${canBet ? '' : ' dp-btn-disabled'}" data-action="bet" ${canBet ? '' : 'disabled'}>${t('dp_place_bet')}</button>
        </div>
      </div>`;
    } else {
      html += `<div class="dp-bet-placed">
        <div class="dp-bet-placed-text">${t('dp_bet_placed')}: ${formatNumber(dp.playerBet!.amount)}</div>
        <div class="dp-bet-placed-hint">${t('dp_waiting_result')}</div>
      </div>`;
    }
  }

  // === REVEAL PHASE ===
  if (dp.phase === 'reveal') {
    const timeLeft = Math.max(0, Math.ceil((dp.phaseEndTime - now) / 1000));
    html += `<div class="dp-phase-label dp-phase-reveal">${t('dp_revealing')} <span class="dp-timer">${timeLeft}s</span></div>`;

    html += `<div class="dp-coins dp-coins-reveal">`;
    for (const coin of dp.coins) {
      const isBet = dp.playerBet?.coinId === coin.id;
      html += `<div class="dp-coin dp-coin-revealing${isBet ? ' dp-coin-bet' : ''}">
        <div class="dp-coin-icon dp-icon-shake">${dpCoinIconHtml(coin)}</div>
        <div class="dp-coin-name">${dpCoinName(coin)}</div>
        <div class="dp-coin-mult">?x</div>
      </div>`;
    }
    html += `</div>`;

    if (dp.playerBet) {
      html += `<div class="dp-bet-placed">
        <div class="dp-bet-placed-text">${t('dp_your_bet')}: ${formatNumber(dp.playerBet.amount)}</div>
      </div>`;
    }
  }

  // === RESULT PHASE ===
  if (dp.phase === 'result' && dp.result) {
    const timeLeft = Math.max(0, Math.ceil((dp.phaseEndTime - now) / 1000));
    const winner = dp.coins.find(c => c.id === dp.result!.winnerId);
    const won = dp.playerBet && dp.playerBet.coinId === dp.result.winnerId;
    const rewardMult = getRewardMultiplier(st.purchasedUpgrades);
    const totalReward = won ? dp.playerBet!.amount * dp.result.multiplier * rewardMult : 0;

    html += `<div class="dp-phase-label dp-phase-result">${t('dp_result')} <span class="dp-timer">${timeLeft}s</span></div>`;

    html += `<div class="dp-coins">`;
    for (const coin of dp.coins) {
      const isWinner = coin.id === dp.result.winnerId;
      const isBet = dp.playerBet?.coinId === coin.id;
      const cls = isWinner ? 'dp-coin dp-coin-winner' : 'dp-coin dp-coin-loser';
      html += `<div class="${cls}">
        <div class="dp-coin-icon">${dpCoinIconHtml(coin)}</div>
        <div class="dp-coin-name">${dpCoinName(coin)}</div>
        ${isWinner ? `<div class="dp-coin-mult">x${dp.result!.multiplier}</div>` : '<div class="dp-coin-mult dp-mult-dim">-</div>'}
        ${isBet ? `<div class="dp-coin-badge">${t('dp_your_bet')}</div>` : ''}
      </div>`;
    }
    html += `</div>`;

    if (dp.playerBet) {
      if (won) {
        html += `<div class="dp-result-win">
          <div class="dp-result-text">${t('dp_you_won')}</div>
          <div class="dp-result-amount">+${formatNumber(totalReward)}</div>
          <button class="dp-btn dp-btn-claim" data-action="claim">${t('dp_claim')}</button>
        </div>`;
      } else {
        html += `<div class="dp-result-lose">
          <div class="dp-result-text">${t('dp_you_lost')}</div>
          <div class="dp-result-amount">-${formatNumber(dp.playerBet.amount)}</div>
        </div>`;
      }
    } else {
      html += `<div class="dp-result-skip">
        <div class="dp-result-text">${t('dp_no_bet')}</div>
        <div class="dp-result-hint">${winner ? dpCoinIconHtml(winner, ' dp-coin-initial-sm') + ' ' + dpCoinName(winner) : '?'} ${t('dp_was_winner')} x${dp.result.multiplier}</div>
      </div>`;
    }
  }

  if (setInnerHTML(container, html)) {
    bindEvents();
  }
}

function bindEvents() {
  if (!container) return;

  // Coin selection
  container.querySelectorAll('[data-coin]').forEach(el => {
    el.addEventListener('click', () => {
      const st = getState();
      if (st.darkPool.playerBet) return; // already bet
      selectedCoinId = (el as HTMLElement).dataset.coin!;
      markScreenDirty();
      requestRender();
    });
  });

  // Bet slider (percent)
  const slider = container.querySelector('#dp-bet-slider') as HTMLInputElement;
  if (slider) {
    slider.addEventListener('input', () => {
      betPercent = parseInt(slider.value, 10);
      markScreenDirty();
      requestRender();
    });
  }

  // Action buttons
  container.querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const action = (btn as HTMLElement).dataset.action;

      if (action === 'bet') {
        if (!selectedCoinId) return;
        const st = getState();
        const maxBet = Math.floor(st.coins * getMaxBetRatio(st.purchasedUpgrades));
        const minBet = Math.floor(st.coins * DARKPOOL_CONFIG.minBetRatio);
        const betAmount = Math.max(minBet, Math.min(Math.floor(st.coins * betPercent / 100), maxBet));
        dispatch({ type: 'DARKPOOL_BET', payload: { coinId: selectedCoinId, amount: betAmount } });
        playSfx('purchase');
        selectedCoinId = null;
        markScreenDirty();
        requestRender();
      }

      if (action === 'insider') {
        const rewards = getAdRewards();
        const result = await AdManager.showRewardedAd(rewards.FREE_DOCS);
        if (result) {
          dispatch({ type: 'DARKPOOL_INSIDER', payload: { watchedAd: true } });
          playSfx('achievement');
          showToast(t('dp_insider_received'));
        }
        markScreenDirty();
        requestRender();
      }

      if (action === 'claim') {
        dispatch({ type: 'DARKPOOL_CLAIM' });
        playSfx('golden');
        markScreenDirty();
        requestRender();
      }

      if (action === 'help') {
        showTutorial();
        return;
      }

      if (action === 'skip') {
        const rewards = getAdRewards();
        const result = await AdManager.showRewardedAd(rewards.FREE_DOCS);
        if (result) {
          dispatch({ type: 'DARKPOOL_SKIP_COOLDOWN' });
          playSfx('boost');
        }
        markScreenDirty();
        requestRender();
      }
    });
  });
}
